import * as Sentry from "@sentry/browser";

import { type roleSchema } from "./GroupSchema";

import type { AlertStatus } from "app/contexts/AlertContext";
import type { PayloadError } from "relay-runtime";
import type * as z from "zod";

interface RoleAssignmentInput {
  roleName: string;
  principal: string;
  target: string;
}

export interface RoleAssignments {
  grant: RoleAssignmentInput[];
  revoke: RoleAssignmentInput[];
}

export type CreatorOptions = NonNullable<
  z.infer<typeof roleSchema>["creatorOptions"]
>;
export type EditorOptions = NonNullable<
  z.infer<typeof roleSchema>["editorOptions"]
>;
export type OwnerOptions = NonNullable<
  z.infer<typeof roleSchema>["ownerOptions"]
>;

export interface Group {
  id: string;
  name: string;
  displayName?: string;
  roleAssignmentPrincipal: string;
}

/**
 * Build role assignments from role data and principal
 */
export function buildRoleAssignments(
  roles: z.infer<typeof roleSchema>[],
  principal: string,
  initialRoleAssignments?:
    | readonly {
        node: {
          id: string;
          role: { name: string };
          target: { roleAssignmentTarget: string };
        };
      }[]
    | null
    | undefined,
): RoleAssignments {
  const result: RoleAssignments = {
    grant: [],
    revoke: [],
  };
  const initialAssignments = new Map<string, Set<string>>();

  initialRoleAssignments?.forEach(({ node }) => {
    const { name: roleName } = node.role;
    const target = node.target.roleAssignmentTarget;

    if (!initialAssignments.has(roleName)) {
      initialAssignments.set(roleName, new Set());
    }
    initialAssignments.get(roleName)?.add(target);
  });

  const addGrantIfNeeded = (roleName: string, target: string) => {
    const existingTargets = initialAssignments.get(roleName);
    // Only add to grant if it wasn't already assigned
    if (!existingTargets?.has(target)) {
      result.grant.push({ roleName, principal, target });
    }
    // Mark this target as processed so we don't revoke it later
    existingTargets?.delete(target);
  };

  const addRevoke = (roleName: string, target: string) => {
    result.revoke.push({ roleName, principal, target });
  };

  const handleSystemAll = (roleName: string, scope: string) => {
    if (scope === "GRANT_SYSTEM_ALL") {
      addGrantIfNeeded(roleName, "system:all");
    } else if (scope === "REVOKE_SYSTEM_ALL") {
      addRevoke(roleName, "system:all");
    }
  };

  const handleCreatorOptions = (options: CreatorOptions, roleName: string) => {
    handleTargetType(options.accountGroups, roleName, "account-group:all");
    handleTargetType(
      options.policyCollections,
      roleName,
      "policy-collection:all",
    );
    handleTargetType(options.policyRepositories, roleName, "repository:all");
  };

  const handleTargetType = (
    enabled: boolean | undefined,
    roleName: string,
    target: string,
  ) => {
    if (enabled) {
      addGrantIfNeeded(roleName, target);
    } else if (initialAssignments.get(roleName)?.has(target)) {
      addRevoke(roleName, target);
      // Remove from initial assignments to prevent duplicate revocations
      initialAssignments.get(roleName)?.delete(target);
    }
  };

  // Helper to handle options for editor/owner roles
  const handleEditorOwnerOptions = (
    options: EditorOptions | OwnerOptions,
    roleName: string,
  ) => {
    const seenTargets = new Set<string>();

    // Process each target type
    ["accountGroups", "policyCollections", "policyRepositories"].forEach(
      (field) => {
        options[field as keyof typeof options]?.forEach(
          (item: { value: string; roleAssignmentTarget: string }) => {
            const target = item.roleAssignmentTarget;

            seenTargets.add(target);
            addGrantIfNeeded(roleName, target);
          },
        );
      },
    );
  };

  // Process current form data to determine grants
  roles.forEach((role) => {
    const { role: roleName, scope } = role;

    switch (roleName) {
      case "admin":
      case "stacklet-user":
      case "viewer":
        handleSystemAll(roleName, scope);
        break;

      case "resource-viewer":
        if (scope === "GRANT_ACCOUNTS_ALL") {
          addGrantIfNeeded(roleName, "account-group:all");
        } else if (scope === "TARGETS_SELECT") {
          // Revoke the "all" grant and add selected account groups
          addRevoke(roleName, "account-group:all");
          // Add selected account groups
          role.resourceViewerOptions?.accountGroups.forEach((accountGroup) => {
            const target = accountGroup.roleAssignmentTarget;
            addGrantIfNeeded(roleName, target);
          });
        }
        break;

      case "policy-deployer":
        if (scope === "GRANT_ACCOUNTS_ALL") {
          addGrantIfNeeded(roleName, "account-group:all");
        } else if (scope === "TARGETS_SELECT") {
          addRevoke(roleName, "account-group:all");
          // Add selected account groups
          role.policyDeployerOptions?.accountGroups.forEach((accountGroup) => {
            const target = accountGroup.roleAssignmentTarget;
            addGrantIfNeeded(roleName, target);
          });
        }
        break;

      case "creator":
        if (scope === "GRANT_SYSTEM_ALL") {
          addGrantIfNeeded(roleName, "system:all");
        } else if (scope === "TARGETS_SELECT") {
          const options: CreatorOptions = role.creatorOptions || {
            accountGroups: false,
            policyCollections: false,
            policyRepositories: false,
          };
          handleCreatorOptions(options, roleName);
        }
        break;

      case "editor":
      case "owner":
        if (scope === "GRANT_SYSTEM_ALL") {
          addGrantIfNeeded(roleName, "system:all");
        } else if (scope === "TARGETS_SELECT") {
          const options =
            roleName === "editor"
              ? role.editorOptions || {
                  accountGroups: [],
                  policyCollections: [],
                  policyRepositories: [],
                }
              : role.ownerOptions || {
                  accountGroups: [],
                  policyCollections: [],
                  policyRepositories: [],
                };

          handleEditorOwnerOptions(options, roleName);
        }
        break;
    }
  });

  // Any remaining initial assignments that weren't processed should be revoked
  initialAssignments.forEach((targets, roleName) => {
    targets.forEach((target) => {
      addRevoke(roleName, target);
    });
  });

  return result;
}

interface RoleAssignmentResult {
  readonly errorMessage?: string | null;
}

/** The parts of an `updateRoleAssignment` payload that report per-item failures */
interface RoleAssignmentPayload {
  readonly grant?: readonly RoleAssignmentResult[] | null;
  readonly revoke?: readonly RoleAssignmentResult[] | null;
}

/**
 * Handle role assignment errors, alerting and returning true if there were any.
 *
 * `updateRoleAssignment` authorizes each grant and revoke on its own (EDIT_ACL
 * on its target), and reports a refused one as an `errorMessage` on that item
 * rather than failing the whole mutation, so the payload has to be checked as
 * well as the request errors.
 */
export function handleRoleAssignmentErrors(
  errors: readonly PayloadError[] | null | undefined,
  alertDispatch: (alert: AlertStatus) => void,
  payload?: RoleAssignmentPayload | null,
  messagePrefix = "Failed to assign roles",
): boolean {
  if (errors?.length) {
    errors.forEach((error) => Sentry.captureException(error));
    alertDispatch({
      message: `${messagePrefix}: ${errors[0].message}`,
      severity: "error",
    });
    return true;
  }

  const itemErrors = [...(payload?.grant ?? []), ...(payload?.revoke ?? [])]
    .map((result) => result.errorMessage)
    .filter((message): message is string => Boolean(message));

  if (itemErrors.length === 0) return false;

  alertDispatch({
    message: `${messagePrefix}: ${itemErrors.join("; ")}`,
    severity: "error",
  });
  return true;
}
